/**
 * Reusable Education + Work-History list/edit sections used by both
 * /profile (self-service) and HR Employees modal.
 *
 * Pass mutation-shaped hooks so the same component drives both
 * /me/profile/* and /hr/employees/:emp/* endpoints.
 */
import { useState } from 'react';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import {
  Plus,
  Pencil,
  Trash2,
  GraduationCap,
  Briefcase,
  X,
  Save,
  FileText,
  Upload,
  Image as ImageIcon,
  Loader2,
} from 'lucide-react';
import type { EducationRow, WorkHistoryRow, DocumentRow } from '../../hooks';
import { uploadFile, resolveFileUrl } from '../../lib/api-client';

const DOCUMENT_PRESETS = [
  'สำเนาทะเบียนบ้าน',
  'สำเนาบัตรประชาชน',
  'หนังสือรับรองการทำงาน',
  'ใบรับรองการศึกษา / Transcript',
  'ใบขับขี่',
  'ใบรับรองอบรม',
  'หน้าสมุดบัญชี',
  'อื่นๆ',
];

const DEGREES = [
  'ม.ต้น',
  'ม.ปลาย',
  'ปวช.',
  'ปวส.',
  'ปริญญาตรี',
  'ปริญญาโท',
  'ปริญญาเอก',
  'อื่นๆ',
] as const;

interface EduSectionProps {
  query: UseQueryResult<EducationRow[]>;
  upsert: UseMutationResult<EducationRow, Error, Partial<EducationRow>>;
  remove: UseMutationResult<unknown, Error, string>;
}

export function EducationSection({ query, upsert, remove }: EduSectionProps) {
  const [editing, setEditing] = useState<Partial<EducationRow> | null>(null);
  const rows = query.data ?? [];

  async function handleSave() {
    if (!editing) return;
    if (!editing.degree || !editing.institution) {
      alert('กรุณากรอกระดับการศึกษา + สถาบัน');
      return;
    }
    try {
      await upsert.mutateAsync(editing);
      setEditing(null);
    } catch (e) {
      alert(`บันทึกไม่สำเร็จ: ${e instanceof Error ? e.message : 'unknown'}`);
    }
  }
  async function handleDelete(id: string) {
    if (!confirm('ลบรายการนี้?')) return;
    await remove.mutateAsync(id);
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-gray-500 flex items-center gap-1">
          <GraduationCap className="w-3.5 h-3.5" /> ประวัติการศึกษา
        </div>
        <button
          type="button"
          onClick={() => setEditing({})}
          className="text-xs text-brand-600 hover:underline flex items-center gap-1"
        >
          <Plus className="w-3.5 h-3.5" /> เพิ่ม
        </button>
      </div>

      {query.isLoading ? (
        <p className="text-xs text-gray-400">กำลังโหลด…</p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-gray-400 italic">ยังไม่มีข้อมูล</p>
      ) : (
        <div className="space-y-1.5">
          {rows.map((r) => (
            <div key={r.id} className="border border-gray-200 rounded-lg p-2 flex items-start gap-2">
              <div className="min-w-0 flex-1 text-sm">
                <div className="font-medium">{r.degree} · {r.institution}</div>
                <div className="text-xs text-gray-500">
                  {r.major && <span>{r.major} · </span>}
                  {r.year_start && r.year_end ? `${r.year_start}–${r.year_end}` : r.year_end ? `จบปี ${r.year_end}` : ''}
                  {r.gpa != null && ` · GPA ${r.gpa}`}
                </div>
              </div>
              <button type="button" onClick={() => setEditing(r)} className="p-1 text-gray-400 hover:text-brand-600">
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button type="button" onClick={() => handleDelete(r.id)} className="p-1 text-gray-400 hover:text-red-600">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="border border-brand-300 rounded-lg p-3 bg-brand-50/30 space-y-2">
          <div className="flex justify-between items-center">
            <div className="text-xs font-semibold text-brand-700">
              {editing.id ? 'แก้ไข' : 'เพิ่ม'} ประวัติการศึกษา
            </div>
            <button type="button" onClick={() => setEditing(null)} className="text-gray-400">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label">ระดับ *</label>
              <select
                className="input"
                value={editing.degree ?? ''}
                onChange={(e) => setEditing({ ...editing, degree: e.target.value })}
              >
                <option value="">— เลือก —</option>
                {DEGREES.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="label">GPA</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="4.5"
                className="input"
                value={editing.gpa ?? ''}
                onChange={(e) => setEditing({ ...editing, gpa: e.target.value === '' ? null : Number(e.target.value) })}
              />
            </div>
          </div>
          <div>
            <label className="label">สถาบัน *</label>
            <input
              className="input"
              value={editing.institution ?? ''}
              onChange={(e) => setEditing({ ...editing, institution: e.target.value })}
            />
          </div>
          <div>
            <label className="label">สาขา/วิชาเอก</label>
            <input
              className="input"
              value={editing.major ?? ''}
              onChange={(e) => setEditing({ ...editing, major: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label">ปีเข้า</label>
              <input
                type="number"
                inputMode="numeric"
                className="input"
                value={editing.year_start ?? ''}
                onChange={(e) => setEditing({ ...editing, year_start: e.target.value === '' ? null : Number(e.target.value) })}
              />
            </div>
            <div>
              <label className="label">ปีจบ</label>
              <input
                type="number"
                inputMode="numeric"
                className="input"
                value={editing.year_end ?? ''}
                onChange={(e) => setEditing({ ...editing, year_end: e.target.value === '' ? null : Number(e.target.value) })}
              />
            </div>
          </div>
          <button
            type="button"
            onClick={handleSave}
            disabled={upsert.isPending}
            className="btn-primary w-full text-sm"
          >
            <Save className="w-4 h-4" />
            {upsert.isPending ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </div>
      )}
    </div>
  );
}

interface WorkSectionProps {
  query: UseQueryResult<WorkHistoryRow[]>;
  upsert: UseMutationResult<WorkHistoryRow, Error, Partial<WorkHistoryRow>>;
  remove: UseMutationResult<unknown, Error, string>;
}

export function WorkHistorySection({ query, upsert, remove }: WorkSectionProps) {
  const [editing, setEditing] = useState<Partial<WorkHistoryRow> | null>(null);
  const rows = query.data ?? [];

  async function handleSave() {
    if (!editing) return;
    if (!editing.company || !editing.position) {
      alert('กรุณากรอกชื่อบริษัท + ตำแหน่ง');
      return;
    }
    try {
      await upsert.mutateAsync(editing);
      setEditing(null);
    } catch (e) {
      alert(`บันทึกไม่สำเร็จ: ${e instanceof Error ? e.message : 'unknown'}`);
    }
  }
  async function handleDelete(id: string) {
    if (!confirm('ลบรายการนี้?')) return;
    await remove.mutateAsync(id);
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-gray-500 flex items-center gap-1">
          <Briefcase className="w-3.5 h-3.5" /> ประวัติการทำงาน
        </div>
        <button
          type="button"
          onClick={() => setEditing({})}
          className="text-xs text-brand-600 hover:underline flex items-center gap-1"
        >
          <Plus className="w-3.5 h-3.5" /> เพิ่ม
        </button>
      </div>

      {query.isLoading ? (
        <p className="text-xs text-gray-400">กำลังโหลด…</p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-gray-400 italic">ยังไม่มีข้อมูล</p>
      ) : (
        <div className="space-y-1.5">
          {rows.map((r) => (
            <div key={r.id} className="border border-gray-200 rounded-lg p-2 flex items-start gap-2">
              <div className="min-w-0 flex-1 text-sm">
                <div className="font-medium">{r.position} · {r.company}</div>
                <div className="text-xs text-gray-500">
                  {r.start_date} – {r.end_date ?? 'ปัจจุบัน'}
                  {r.reason_for_leaving && ` · ${r.reason_for_leaving}`}
                </div>
              </div>
              <button type="button" onClick={() => setEditing(r)} className="p-1 text-gray-400 hover:text-brand-600">
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button type="button" onClick={() => handleDelete(r.id)} className="p-1 text-gray-400 hover:text-red-600">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="border border-brand-300 rounded-lg p-3 bg-brand-50/30 space-y-2">
          <div className="flex justify-between items-center">
            <div className="text-xs font-semibold text-brand-700">
              {editing.id ? 'แก้ไข' : 'เพิ่ม'} ประวัติการทำงาน
            </div>
            <button type="button" onClick={() => setEditing(null)} className="text-gray-400">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div>
            <label className="label">บริษัท/หน่วยงาน *</label>
            <input
              className="input"
              value={editing.company ?? ''}
              onChange={(e) => setEditing({ ...editing, company: e.target.value })}
            />
          </div>
          <div>
            <label className="label">ตำแหน่ง *</label>
            <input
              className="input"
              value={editing.position ?? ''}
              onChange={(e) => setEditing({ ...editing, position: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label">วันเริ่มงาน</label>
              <input
                type="date"
                className="input"
                value={editing.start_date ?? ''}
                onChange={(e) => setEditing({ ...editing, start_date: e.target.value || null })}
              />
            </div>
            <div>
              <label className="label">วันสิ้นสุด</label>
              <input
                type="date"
                className="input"
                value={editing.end_date ?? ''}
                onChange={(e) => setEditing({ ...editing, end_date: e.target.value || null })}
              />
            </div>
          </div>
          <div>
            <label className="label">เหตุผลที่ลาออก</label>
            <input
              className="input"
              value={editing.reason_for_leaving ?? ''}
              onChange={(e) => setEditing({ ...editing, reason_for_leaving: e.target.value })}
            />
          </div>
          <button
            type="button"
            onClick={handleSave}
            disabled={upsert.isPending}
            className="btn-primary w-full text-sm"
          >
            <Save className="w-4 h-4" />
            {upsert.isPending ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </div>
      )}
    </div>
  );
}

interface DocsSectionProps {
  query: UseQueryResult<DocumentRow[]>;
  create: UseMutationResult<DocumentRow, Error, { label: string; r2_path: string; mime_type?: string; size_bytes?: number; note?: string }>;
  remove: UseMutationResult<unknown, Error, string>;
  /** false = พนักงานดูของตัวเอง: แนบเพิ่มได้ แต่ลบไม่ได้ (ให้ HR ลบ) */
  canDelete?: boolean;
}

export function DocumentsSection({ query, create, remove, canDelete = true }: DocsSectionProps) {
  const rows = query.data ?? [];
  const [adding, setAdding] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingPath, setPendingPath] = useState<string | null>(null);
  const [pendingPreview, setPendingPreview] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const [labelPreset, setLabelPreset] = useState('');
  const [note, setNote] = useState('');
  const [uploading, setUploading] = useState(false);

  function reset() {
    setAdding(false);
    setPendingFile(null);
    setPendingPath(null);
    setPendingPreview(null);
    setLabel('');
    setLabelPreset('');
    setNote('');
  }

  async function handleFile(file: File | null) {
    if (!file) return;
    setPendingFile(file);
    setPendingPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const path = await uploadFile(file, 'identity', file.name);
      setPendingPath(path);
    } catch (e) {
      alert(`อัปโหลดไม่สำเร็จ: ${e instanceof Error ? e.message : 'unknown'}`);
      setPendingFile(null);
      setPendingPreview(null);
    } finally {
      setUploading(false);
    }
  }

  async function handleSave() {
    const finalLabel = (labelPreset === 'อื่นๆ' ? label : labelPreset) || label;
    if (!finalLabel) {
      alert('กรุณาเลือก/กรอกชื่อเอกสาร');
      return;
    }
    if (!pendingPath || !pendingFile) {
      alert('กรุณาเลือกไฟล์');
      return;
    }
    try {
      await create.mutateAsync({
        label: finalLabel,
        r2_path: pendingPath,
        mime_type: pendingFile.type || undefined,
        size_bytes: pendingFile.size,
        note: note || undefined,
      });
      reset();
    } catch (e) {
      alert(`บันทึกไม่สำเร็จ: ${e instanceof Error ? e.message : 'unknown'}`);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('ลบเอกสารนี้?')) return;
    try {
      await remove.mutateAsync(id);
    } catch (e) {
      alert(`ลบไม่สำเร็จ: ${e instanceof Error ? e.message : 'unknown'}`);
    }
  }

  function formatSize(bytes: number | null): string {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  function isImage(row: DocumentRow): boolean {
    return row.mime_type?.startsWith('image/') ?? false;
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-gray-500 flex items-center gap-1">
          <FileText className="w-3.5 h-3.5" /> เอกสารแนบอื่นๆ
        </div>
        {!adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="text-xs text-brand-600 hover:underline flex items-center gap-1"
          >
            <Plus className="w-3.5 h-3.5" /> เพิ่มไฟล์
          </button>
        )}
      </div>

      {query.isLoading ? (
        <p className="text-xs text-gray-400">กำลังโหลด…</p>
      ) : rows.length === 0 && !adding ? (
        <p className="text-xs text-gray-400 italic">ยังไม่มีเอกสารแนบ</p>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
          {rows.map((r) => {
            const absUrl = resolveFileUrl(r.url);
            return (
              <div key={r.id} className="border border-gray-200 rounded-lg p-2 relative group">
                <a
                  href={absUrl ?? '#'}
                  target="_blank"
                  rel="noreferrer"
                  className="block aspect-[4/3] bg-gray-50 rounded overflow-hidden mb-1"
                >
                  {isImage(r) && absUrl ? (
                    <img src={absUrl} alt={r.label} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                      <FileText className="w-8 h-8 mb-1" />
                      <span className="text-[10px] uppercase">{r.mime_type?.split('/').pop() ?? 'file'}</span>
                    </div>
                  )}
                </a>
                <div className="text-xs font-medium truncate" title={r.label}>{r.label}</div>
                <div className="text-[10px] text-gray-400">
                  {formatSize(r.size_bytes)} · {new Date(r.uploaded_at).toLocaleDateString('th-TH')}
                </div>
                {r.note && <div className="text-[10px] text-gray-500 italic truncate">{r.note}</div>}
                {canDelete && (
                <button
                  type="button"
                  onClick={() => handleDelete(r.id)}
                  className="absolute top-1 right-1 p-1 bg-white/90 rounded-full opacity-0 group-hover:opacity-100 transition shadow"
                  title="ลบ"
                >
                  <Trash2 className="w-3.5 h-3.5 text-red-600" />
                </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {adding && (
        <div className="border border-brand-300 rounded-lg p-3 bg-brand-50/30 space-y-2 mt-2">
          <div className="flex justify-between items-center">
            <div className="text-xs font-semibold text-brand-700">เพิ่มเอกสาร</div>
            <button type="button" onClick={reset} className="text-gray-400">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* File picker / preview */}
          <label className="block border-2 border-dashed border-gray-300 rounded-lg p-2 cursor-pointer hover:bg-white transition relative aspect-[16/9] overflow-hidden">
            <input
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
            />
            {pendingPreview ? (
              pendingFile?.type.startsWith('image/') ? (
                <img src={pendingPreview} alt="" className="absolute inset-0 w-full h-full object-contain" />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500">
                  <FileText className="w-8 h-8 mb-1" />
                  <span className="text-xs">{pendingFile?.name}</span>
                </div>
              )
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
                <ImageIcon className="w-8 h-8 mb-1" />
                <span className="text-xs">แตะเพื่อเลือกไฟล์ (รูปหรือ PDF)</span>
              </div>
            )}
            {uploading && (
              <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-white animate-spin" />
              </div>
            )}
            <div className="absolute bottom-1 right-1 bg-white/90 rounded-full p-1 shadow">
              <Upload className="w-3.5 h-3.5 text-brand-600" />
            </div>
          </label>

          {/* Label */}
          <div>
            <label className="label">ชื่อเอกสาร *</label>
            <select
              className="input"
              value={labelPreset}
              onChange={(e) => {
                setLabelPreset(e.target.value);
                if (e.target.value !== 'อื่นๆ') setLabel('');
              }}
            >
              <option value="">— เลือก —</option>
              {DOCUMENT_PRESETS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            {labelPreset === 'อื่นๆ' && (
              <input
                placeholder="ระบุชื่อเอกสาร"
                className="input mt-2"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            )}
          </div>

          {/* Note */}
          <div>
            <label className="label">หมายเหตุ</label>
            <input
              className="input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="เช่น ออกเมื่อ 2566"
            />
          </div>

          <button
            type="button"
            onClick={handleSave}
            disabled={create.isPending || uploading || !pendingPath}
            className="btn-primary w-full text-sm"
          >
            <Save className="w-4 h-4" />
            {create.isPending ? 'กำลังบันทึก…' : uploading ? 'กำลังอัปโหลด…' : 'บันทึก'}
          </button>
        </div>
      )}
    </div>
  );
}
